<script setup lang="ts">
  import {
    computed,
    inject,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
    watchEffect
  } from 'vue'
  import { pendingRefocusKey } from '@/shared/models/pendingRefocus'
  import {
    Form,
    Input,
    InputNumber,
    Select,
    SelectOption,
    Checkbox,
    Button,
    AutoComplete
  } from 'ant-design-vue'
  import { PlotSchema } from '../models/PlotSchema'
  import { PlotField } from '../models/PlotField'
  import { PlotFieldType } from '../models/PlotFieldType'
  import { PlottingService } from '../services/PlottingService'
  import { debounceTime, Subject } from 'rxjs'
  import { parseSmartValue, DEFAULT_INPUT_DEBOUNCE_MS } from '@/utils'
  import { MinusSquare, PlusSquare } from 'lucide-vue-next'
  import ArrayPlotBuilder from './ArrayPlotBuilder.vue'
  import JsonPlotConfiguration from './JsonPlotConfiguration.vue'
  import { PlottingJsonPlotConfigurationService } from '../services/PlottingJsonPlotConfigurationService'
  import SelectOrCreatePlotConfiguration from './SelectOrCreatePlotConfiguration.vue'
  import ColumnMultiSelectOrCreatePlotConfiguration from './ColumnMultiSelectOrCreatePlotConfiguration.vue'
  import { CompressOutlined, ExpandOutlined } from '@ant-design/icons-vue'
  import PlotHelpPopover from '../help/components/PlotHelpPopover.vue'
  import RegexPlottingConfiguration from './RegexPlottingConfiguration.vue'
  import ColorPlotConfiguration from './ColorPlotConfiguration.vue'
  import StringTemplateBuilder from '@/shared/StringTemplateBuilder/components/StringTemplateBuilder.vue'
  import { useWindowSize } from '@/composables/useWindowSize'
  import { PlotDataSourceType } from '../models/PlotDataSourceType'
  import PlotDataSourceSelectConfiguration from './PlotDataSourceSelectConfiguration.vue'

  import { PlotsUiService } from '../services/PlotsUiService'
  import { Plot } from '../models/Plot'

  interface IObjectPlotBuilderProps {
    plotSchemas: Array<PlotSchema>
    plotField: PlotField
    modelValue: {}
    availableColumns: Array<{ value: string; label: string }>
    availableParams: Array<{ value: string; label: string }>
    availableDataSources: Array<{
      id: string
      value: string
      label: string
      type: PlotDataSourceType
    }>
    configurationLevel: number
    isCondensedLayout: boolean
    globalCollapsed?: { collapsed: boolean }
    rootPlot?: Plot | null
    currentPath?: string[]
  }

  interface IObjectPlotBuilderEmits {
    (e: 'update:modelValue', value: {}): void
    (e: 'data-source-selected', value: PlotDataSourceType): void
  }

  const props = defineProps<IObjectPlotBuilderProps>()
  const emit = defineEmits<IObjectPlotBuilderEmits>()

  const modelValue = reactive<{}>(props.modelValue)
  const configuration = ref<Record<string, any>>({})
  const isCollapsed = ref<boolean>(false)
  const globalCollapsed = ref<{ collapsed: boolean }>(props.globalCollapsed ?? { collapsed: false })
  const { controlSize } = useWindowSize()

  // Initialize configuration with default values
  const initializeConfiguration = () => {
    if (props.plotField.field_type !== PlotFieldType.Object) return
    // Support nested object structures
    if (
      (props.plotField.item_schema == null && props.plotField.fields == null) ||
      (props.plotField.item_schema != null &&
        props.plotField.item_schema.field_type !== PlotFieldType.Object)
    )
      throw new Error(
        'ObjectPlotBuilder requires an Object type with fields or child items_schema of type'
      )

    if (props.plotField.fields != null) {
      configuration.value = PlottingService.initializePlotFieldConfiguration(
        props.plotField.fields!
      )
    } else if (props.plotField.item_schema != null) {
      configuration.value = PlottingService.initializePlotConfigurationForField(
        props.plotField.item_schema
      )
    }
  }

  const handleConfigurationChange = () => {
    if (props.plotField) {
      emit('update:modelValue', configuration.value)
    }
  }

  const handleDataSourceSelected = (value: PlotDataSourceType) => {
    emit('data-source-selected', value)
  }

  // @ts-expect-error
  const handlePlotTypeChanged = (ruleType: string, value: {}) => {
    //TODO: Support object nested custom rules and type conversion
    //if (props.rule) {
    //emit('configuration-updated', { ...props.rule, type: ruleType }, value, true)
    //}
  }

  const pendingRefocus = inject(pendingRefocusKey)

  let isUnmounting = false

  const onInputChange = (newValue: string, fieldName: string) => {
    const activeEl = document.activeElement as HTMLInputElement | null
    if (pendingRefocus) {
      pendingRefocus.value = {
        levelFieldKey: `${props.configurationLevel}:${fieldName}`,
        cursor: activeEl?.selectionStart ?? null
      }
    }
    inputValueChanged$.next({ value: newValue, field: fieldName })
  }

  const onInputBlur = (fieldKey: string) => {
    if (isUnmounting) return
    if (pendingRefocus?.value?.levelFieldKey === `${props.configurationLevel}:${fieldKey}`) {
      pendingRefocus.value = null
    }
  }

  const inputValueChanged$ = new Subject<{ field: string; value: string }>()

  const inputValueChangedSubscription = inputValueChanged$
    .pipe(debounceTime(DEFAULT_INPUT_DEBOUNCE_MS))
    .subscribe(({ field, value }) => {
      const parsedValue = parseSmartValue(value)
      configuration.value[field] = parsedValue
      handleConfigurationChange()
    })

  // Watch for rule changes
  watch(
    modelValue,
    (newVal) => {
      configuration.value = { ...newVal }
    },
    { immediate: true }
  )

  watch(
    () => props.plotField,
    (newPlotField) => {
      if (newPlotField) {
        if (Object.keys(configuration.value).length === 0) {
          initializeConfiguration()
          handleConfigurationChange()
        }
      }
    },
    { immediate: true }
  )

  watchEffect(() => {
    const newVal = props.globalCollapsed
    if (newVal) {
      globalCollapsed.value = newVal
      isCollapsed.value = !!newVal.collapsed
    }
  })

  const toggleCollapse = () => {
    isCollapsed.value = !isCollapsed.value
  }

  const toggleGlobalCollapse = () => {
    globalCollapsed.value = { collapsed: !isCollapsed.value }
    isCollapsed.value = !isCollapsed.value
  }

  // If only single field, do not use grid css
  const simpleFieldCount = computed(() =>
    props?.plotField?.fields
      ? props.plotField.fields.filter(
          (f: any) =>
            f.field_type !== PlotFieldType.Object &&
            f.field_type !== PlotFieldType.Array &&
            f.field_type !== PlotFieldType.Json
        ).length
      : 0
  )

  const shouldShowGlobalCollapseButton = computed(() => {
    return (
      props.plotField?.fields?.findIndex(
        (x: PlotField) =>
          x.field_type === PlotFieldType.Array ||
          x.field_type === PlotFieldType.Object ||
          x.field_type === PlotFieldType.Json
      ) !== -1
    )
  })

  const shouldShowFieldInformation = (field: PlotField): boolean => {
    return field.field_type !== PlotFieldType.Object && field.field_type !== PlotFieldType.Json
  }

  const isObjectWithNestedObject = computed(
    () => props.plotField.fields == null && props.plotField.item_schema != null
  )

  const checkboxSizeClass = computed(() => {
    if (controlSize.value === 'small') return 'checkbox-sm'
    if (controlSize.value === 'large') return 'checkbox-lg'
    return 'checkbox-md'
  })

  const displayField = computed(() => {
    const mappedField = props.plotField?.display_field as string | string[] | null | undefined
    if (mappedField == null) return undefined

    const fields = props.plotField?.fields ?? []

    const valueForName = (name: string) => {
      const matchingField = fields.find((x) => x.name === name)
      if (!matchingField) return undefined
      const val = configuration.value?.[matchingField.name]
      return val === undefined ? undefined : val
    }

    if (typeof mappedField === 'string') {
      return valueForName(mappedField)
    }

    if (Array.isArray(mappedField)) {
      for (const name of mappedField) {
        const v = valueForName(name)
        if (v) return v
      }
    }

    return undefined
  })

  onMounted(() => {
    if (pendingRefocus?.value) {
      const { levelFieldKey, cursor } = pendingRefocus.value
      let el = document.querySelector(`[data-field="${levelFieldKey}"]`) as HTMLElement | null
      if (el && el.tagName !== 'INPUT') el = el.querySelector('input') ?? el
      const inputEl = el as HTMLInputElement | null
      if (inputEl?.tagName === 'INPUT') {
        inputEl.focus()
        if (cursor !== null) {
          try {
            inputEl.setSelectionRange(cursor, cursor)
          } catch {}
        }
      }
      pendingRefocus.value = null
    }
  })

  onBeforeUnmount(() => {
    isUnmounting = true
    inputValueChangedSubscription?.unsubscribe()
  })
</script>

<template>
  <div v-if="isObjectWithNestedObject" class="object-plot-builder-grid-span">
    <ObjectPlotBuilder
      :configuration-level="props.configurationLevel"
      :available-columns="props.availableColumns"
      :available-params="props.availableParams"
      :available-data-sources="props.availableDataSources"
      v-model="configuration[props.plotField.item_schema!.name]"
      :plot-field="props.plotField.item_schema!"
      @update:modelValue="handleConfigurationChange"
      @data-source-selected="handleDataSourceSelected"
      :plot-schemas="plotSchemas"
      :is-condensed-layout="props.isCondensedLayout"
      :root-plot="props.rootPlot"
    />
  </div>

  <div v-else>
    <div
      v-if="plotField"
      :class="[
        'border',
        'plot-configuration',
        'h-full',
        'w-full',
        {
          'child-plot-configuration': props.configurationLevel > 1,
          '!bg-[var(--card)] dark:!bg-[var(--card-light)]': props.configurationLevel % 2 !== 0,
          '!bg-[var(--card-light)] dark:!bg-[var(--card)]': props.configurationLevel % 2 === 0
        }
      ]"
    >
      <div class="flex h-full flex-col">
        <!-- Plot Header -->
        <div class="horizontal-rule mb-4 border-b pb-1">
          <div class="flex">
            <div>
              <h3 class="mb-1 text-lg font-semibold">
                Configure: {{ plotField.display_name }}
                <span v-if="displayField != null">{{ `(${displayField})` }}</span>
              </h3>
            </div>

            <div class="ml-auto flex" v-if="props.configurationLevel > 1">
              <a-tooltip placement="top">
                <template #title>Toggle Collapse</template>
                <Button
                  style="padding: 3px; border: none"
                  type="text"
                  :size="controlSize"
                  :outlined="false"
                  class="flex items-center justify-center"
                  @click="toggleCollapse"
                  aria-label="Toggle Collapse"
                >
                  <PlusSquare
                    v-if="isCollapsed"
                    class="text-base"
                    style="line-height: 1; margin: 0"
                  />
                  <MinusSquare v-else class="text-base" style="line-height: 1; margin: 0" />
                </Button>
              </a-tooltip>

              <a-tooltip v-if="shouldShowGlobalCollapseButton" placement="top">
                <template #title>{{ isCollapsed ? 'Expand All' : 'Collapse All' }}</template>
                <Button
                  style="padding: 3px; border: none"
                  type="text"
                  :size="controlSize"
                  class="flex items-center justify-center"
                  @click="toggleGlobalCollapse"
                  aria-label="Toggle Collapse All"
                >
                  <ExpandOutlined
                    v-if="isCollapsed"
                    class="rotate-90 rounded-sm border-gray-300 p-1 text-base"
                    style="line-height: 1; margin: 0"
                  />
                  <CompressOutlined
                    v-else
                    class="rotate-90 rounded-sm border-gray-300 p-1 text-base"
                    style="line-height: 1; margin: 0"
                  />
                </Button>
              </a-tooltip>
            </div>
          </div>
        </div>

        <!-- Configuration Form -->
        <div
          :class="['flex-1', 'overflow-y-auto', { 'collapsed-plot-configuration': isCollapsed }]"
        >
          <Form ref="formRef" :model="configuration" layout="vertical">
            <div :class="{ 'object-plot-fields-grid': true, expanded: !props.isCondensedLayout }">
              <div
                v-for="field in plotField.fields"
                :key="field.name"
                class="object-plot-form-field-grid"
                :class="{
                  'array-plot-builder-grid-span': field.field_type === PlotFieldType.Array,
                  'object-plot-builder-grid-span': field.field_type === PlotFieldType.Object,
                  'json-plot-configuration-grid-span': field.field_type === PlotFieldType.Json,
                  'single-field-grid-span':
                    simpleFieldCount <= 1 &&
                    field.field_type !== PlotFieldType.Object &&
                    field.field_type !== PlotFieldType.Array &&
                    field.field_type !== PlotFieldType.Json,
                  '!hidden': !PlotsUiService.isFieldVisibleFromRoot(
                    field,
                    props.rootPlot,
                    props.currentPath ? [...props.currentPath, field.name] : [field.name]
                  )
                }"
              >
                <div class="flex">
                  <div v-if="shouldShowFieldInformation(field)" class="min-w-0 flex-1">
                    <label class="table-field-label mb-1 block text-sm font-medium">
                      {{ field.display_name }}
                      <span v-if="field.required" class="text-red-500">*</span>
                    </label>
                    <p v-if="field.description" class="table-field-label mb-2 text-xs">
                      {{ field.description }}
                    </p>
                  </div>

                  <div
                    v-if="field.help_content != null && field.help_content?.length > 0"
                    class="m1-auto flex justify-end"
                  >
                    <PlotHelpPopover
                      :content-type="field.help_content"
                      :title="`${field.display_name} Help`"
                    />
                  </div>
                </div>
                <!-- Text Input -->
                <Input
                  v-if="field.field_type === PlotFieldType.Text"
                  :value="configuration[field.name]"
                  @update:value="(val: string) => onInputChange(val, field.name)"
                  @blur="() => onInputBlur(field.name)"
                  :data-field="`${props.configurationLevel}:${field.name}`"
                  :placeholder="field.placeholder || ''"
                  :readonly="field.readonly"
                  :size="controlSize"
                />

                <!-- Number Input -->
                <InputNumber
                  v-else-if="field.field_type === PlotFieldType.Number"
                  v-model:value="configuration[field.name]"
                  :placeholder="field.placeholder || ''"
                  :size="controlSize"
                  @change="handleConfigurationChange"
                  :disabled="field.readonly"
                  class="w-full"
                />

                <!-- Regex Input -->
                <RegexPlottingConfiguration
                  v-else-if="field.field_type === PlotFieldType.RegularExpression"
                  v-model="configuration[field.name]"
                  :required="field.required"
                  :regex="field.field_type_args?.regex_pattern"
                  :data-type="field.field_type_args?.regex_data_type"
                  :placeholder="field.placeholder || ''"
                  :error-message="field.field_type_args?.regex_description"
                  @update:modelValue="handleConfigurationChange"
                />

                <!-- Select Dropdown -->
                <Select
                  v-else-if="field.field_type === PlotFieldType.Select"
                  v-model:value="configuration[field.name]"
                  :placeholder="`Select ${field.display_name.toLowerCase()}`"
                  @change="handleConfigurationChange"
                  :size="controlSize"
                  :disabled="field.readonly"
                  class="w-full"
                  showSearch
                  allowClear
                >
                  <SelectOption
                    v-for="option in field.options"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </SelectOption>
                </Select>

                <!-- Select Dropdown JSON Payload -->
                <Select
                  v-else-if="field.field_type === PlotFieldType.SelectJson"
                  :value="JSON.stringify(configuration[field.name])"
                  :placeholder="`Select ${field.display_name.toLowerCase()}`"
                  @update:value="
                    (val: string) => {
                      try {
                        const parsed = JSON.parse(val)
                        configuration[field.name] = parsed
                        handleConfigurationChange()
                      } catch (e) {
                        console.error('Failed to parse selected JSON value', e)
                      }
                    }
                  "
                  :size="controlSize"
                  :disabled="field.readonly"
                  class="w-full"
                  showSearch
                  allowClear
                >
                  <SelectOption
                    v-for="option in field.options"
                    :key="JSON.stringify(option.value)"
                    :value="JSON.stringify(option.value)"
                  >
                    <span v-html="option.label"></span>
                  </SelectOption>
                </Select>

                <!-- Select Dropdown Or Create -->
                <SelectOrCreatePlotConfiguration
                  v-else-if="field.field_type === PlotFieldType.SelectOrCreate"
                  v-model="configuration[field.name]"
                  :placeholder="`Select or enter new ${field.display_name.toLowerCase()}`"
                  :options="field.options || []"
                  :disabled="field.readonly"
                  @update:modelValue="handleConfigurationChange"
                />

                <!-- Multi-Select -->
                <Select
                  v-else-if="
                    field.field_type === PlotFieldType.MultiSelect ||
                    field.field_type === PlotFieldType.ColumnMultiSelect ||
                    field.field_type === PlotFieldType.ParamMultiSelect ||
                    field.field_type === PlotFieldType.ParamOrColumnMultiSelect
                  "
                  v-model:value="configuration[field.name]"
                  mode="multiple"
                  :placeholder="`Select ${field.display_name.toLowerCase()}`"
                  @change="handleConfigurationChange"
                  :size="controlSize"
                  :disabled="field.readonly"
                  class="w-full"
                  showSearch
                  allowClear
                >
                  <SelectOption
                    v-if="field.field_type === PlotFieldType.MultiSelect"
                    v-for="option in field.options"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </SelectOption>

                  <SelectOption
                    v-if="field.field_type === PlotFieldType.ColumnMultiSelect"
                    v-for="option in props.availableColumns"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </SelectOption>

                  <SelectOption
                    v-if="field.field_type === PlotFieldType.ParamMultiSelect"
                    v-for="option in props.availableParams"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </SelectOption>

                  <SelectOption
                    v-if="field.field_type === PlotFieldType.ParamOrColumnMultiSelect"
                    v-for="option in [...props.availableColumns, ...props.availableParams]"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </SelectOption>
                </Select>

                <!-- Boolean Checkbox -->
                <Checkbox
                  v-else-if="field.field_type === PlotFieldType.Boolean"
                  @change="handleConfigurationChange"
                  v-model:checked="configuration[field.name]"
                  :disabled="field.readonly"
                  :class="checkboxSizeClass"
                >
                  {{ field.display_name }}
                </Checkbox>

                <!-- Column Select -->
                <Select
                  v-else-if="field.field_type === PlotFieldType.ColumnSelect"
                  v-model:value="configuration[field.name]"
                  :placeholder="`Select ${field.display_name.toLowerCase()}`"
                  @change="handleConfigurationChange"
                  :size="controlSize"
                  :disabled="field.readonly"
                  class="w-full"
                  showSearch
                  allowClear
                >
                  <SelectOption
                    v-for="column in props.availableColumns"
                    :key="column.value"
                    :value="column.value"
                  >
                    {{ column.label }}
                  </SelectOption>
                </Select>

                <!-- Param Select -->
                <Select
                  v-else-if="field.field_type === PlotFieldType.ParamSelect"
                  v-model:value="configuration[field.name]"
                  :placeholder="`Select ${field.display_name.toLowerCase()}`"
                  @change="handleConfigurationChange"
                  :size="controlSize"
                  :disabled="field.readonly"
                  class="w-full"
                  showSearch
                  allowClear
                >
                  <SelectOption
                    v-for="column in props.availableParams"
                    :key="column.value"
                    :value="column.value"
                  >
                    {{ column.label }}
                  </SelectOption>
                </Select>

                <!-- Param or Column Select -->
                <Select
                  v-else-if="field.field_type === PlotFieldType.ParamOrColumnSelect"
                  v-model:value="configuration[field.name]"
                  :placeholder="`Select ${field.display_name.toLowerCase()}`"
                  @change="handleConfigurationChange"
                  :size="controlSize"
                  :disabled="field.readonly"
                  class="w-full"
                  showSearch
                  allowClear
                >
                  <SelectOption
                    v-for="column in [...props.availableColumns, ...props.availableParams]"
                    :key="column.value"
                    :value="column.value"
                  >
                    {{ column.label }}
                  </SelectOption>
                </Select>

                <!-- Column Select or Create -->
                <AutoComplete
                  v-else-if="field.field_type === PlotFieldType.ColumnSelectOrCreate"
                  v-model:value="configuration[field.name]"
                  :options="props.availableColumns"
                  :placeholder="
                    field.placeholder || `Select existing column or enter new column name`
                  "
                  :size="controlSize"
                  class="w-full"
                  :disabled="field.readonly"
                  @change="handleConfigurationChange"
                  showSearch
                  allowClear
                />

                <!-- Param Select or Create -->
                <AutoComplete
                  v-else-if="field.field_type === PlotFieldType.ParamSelectOrCreate"
                  v-model:value="configuration[field.name]"
                  :options="props.availableParams"
                  :placeholder="
                    field.placeholder || `Select existing param or enter new param name`
                  "
                  :size="controlSize"
                  class="w-full"
                  :disabled="field.readonly"
                  @change="handleConfigurationChange"
                  showSearch
                  allowClear
                />

                <!-- Param or Column Select or Create -->
                <AutoComplete
                  v-else-if="field.field_type === PlotFieldType.ParamOrColumnSelectOrCreate"
                  v-model:value="configuration[field.name]"
                  :options="[...props.availableColumns, ...props.availableParams]"
                  :placeholder="
                    field.placeholder || `Select existing param or column enter new value`
                  "
                  :size="controlSize"
                  class="w-full"
                  :disabled="field.readonly"
                  @change="handleConfigurationChange"
                  showSearch
                  allowClear
                />

                <!-- Multi Column Select Or Create -->
                <ColumnMultiSelectOrCreatePlotConfiguration
                  v-else-if="field.field_type === PlotFieldType.ColumnMultiSelectOrCreate"
                  v-model="configuration[field.name]"
                  :placeholder="`Select or enter new ${field.display_name.toLowerCase()} values`"
                  :available-columns="props.availableColumns"
                  :disabled="field.readonly"
                  @update:modelValue="handleConfigurationChange"
                />

                <!-- Multi Param Select Or Create -->
                <ColumnMultiSelectOrCreatePlotConfiguration
                  v-else-if="field.field_type === PlotFieldType.ParamMultiSelectOrCreate"
                  v-model="configuration[field.name]"
                  :placeholder="`Select or enter new ${field.display_name.toLowerCase()} values`"
                  :available-columns="props.availableParams"
                  :disabled="field.readonly"
                  @update:modelValue="handleConfigurationChange"
                />

                <!-- Param Or Column Multi Select Or Create -->
                <ColumnMultiSelectOrCreatePlotConfiguration
                  v-else-if="field.field_type === PlotFieldType.ParamOrColumnMultiSelectOrCreate"
                  v-model="configuration[field.name]"
                  :placeholder="`Select or enter new ${field.display_name.toLowerCase()} values`"
                  :available-columns="[...props.availableColumns, ...props.availableParams]"
                  :disabled="field.readonly"
                  @update:modelValue="handleConfigurationChange"
                />

                <!-- Color Picker -->
                <ColorPlotConfiguration
                  v-else-if="field.field_type === PlotFieldType.ColorPicker"
                  v-model="configuration[field.name]"
                  :place-holder="field.placeholder || 'Select color'"
                  @update:modelValue="handleConfigurationChange"
                />

                <!-- Select DataSource -->
                <PlotDataSourceSelectConfiguration
                  v-else-if="field.field_type === PlotFieldType.SelectDataSource"
                  v-model="configuration[field.name]"
                  :placeholder="`Select ${field.display_name.toLowerCase()} value`"
                  :available-data-sources="props.availableDataSources"
                  :data-source-types="field.field_type_args.datasource_types"
                  :disabled="field.readonly"
                  @update:modelValue="handleConfigurationChange"
                  @data-source-selected="handleDataSourceSelected"
                />

                <!-- Array Type -->
                <ArrayPlotBuilder
                  v-else-if="field.field_type === PlotFieldType.Array"
                  :configuration-level="props.configurationLevel"
                  :available-columns="props.availableColumns"
                  :available-params="props.availableParams"
                  :available-data-sources="props.availableDataSources"
                  v-model="configuration[field.name]"
                  :plot-field="field"
                  @update:modelValue="handleConfigurationChange"
                  @data-source-selected="handleDataSourceSelected"
                  :plot-schemas="plotSchemas"
                  :is-condensed-layout="props.isCondensedLayout"
                />

                <!-- Object Type (Nested) -->
                <ObjectPlotBuilder
                  v-else-if="field.field_type === PlotFieldType.Object"
                  :configuration-level="props.configurationLevel + 1"
                  :available-columns="props.availableColumns"
                  :available-params="props.availableParams"
                  :available-data-sources="props.availableDataSources"
                  v-model="configuration[field.name]"
                  :plot-field="field"
                  @update:modelValue="handleConfigurationChange"
                  @data-source-selected="handleDataSourceSelected"
                  :plot-schemas="plotSchemas"
                  :is-condensed-layout="props.isCondensedLayout"
                  :root-plot="props.rootPlot"
                  :current-path="
                    props.currentPath ? [...props.currentPath, field.name] : [field.name]
                  "
                />

                <!-- String Template -->
                <StringTemplateBuilder
                  v-else-if="field.field_type === PlotFieldType.StringTemplate"
                  :configuration-level="props.configurationLevel + 1"
                  :available-columns="props.availableColumns"
                  :delimiter_start="field.field_type_args?.delimiter_start"
                  :delimiter_end="field.field_type_args?.delimiter_end"
                  v-model="configuration[field.name]"
                  :place-holder="field.placeholder"
                  @update:modelValue="handleConfigurationChange"
                  :is-condensed-layout="props.isCondensedLayout"
                  :global-collapsed="globalCollapsed"
                />

                <!-- Json Type (Custom Rule) -->
                <JsonPlotConfiguration
                  v-else-if="field.field_type === PlotFieldType.Json"
                  :plot-schemas="props.plotSchemas"
                  :available-columns="props.availableColumns"
                  v-model="configuration"
                  :editor-schema="PlottingJsonPlotConfigurationService.getJsonEditorSchemaForPlot()"
                  :place-holder="field.placeholder || 'Configure Json'"
                  :configuration-level="props.configurationLevel"
                  @plot-type-changed="handlePlotTypeChanged"
                  @update:modelValue="handleConfigurationChange"
                  :allow-type-change="true"
                />

                <!-- Default fallback for unknown field types -->
                <JsonPlotConfiguration
                  v-else
                  :plot-schemas="props.plotSchemas"
                  :available-columns="props.availableColumns"
                  v-model="configuration[field.name]"
                  :editor-schema="
                    PlottingJsonPlotConfigurationService.getJsonEditorSchemaForField(field)
                  "
                  :place-holder="field.placeholder || 'Configure Json'"
                  :configuration-level="props.configurationLevel"
                  @update:modelValue="handleConfigurationChange"
                  :allow-type-change="false"
                />
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .collapsed-plot-configuration {
    display: none;
  }

  .plot-configuration {
    border: 1px solid #0000002e;
    padding: 10px;
  }

  .horizontal-rule {
    border-bottom-color: #0000001f;
  }

  /* Custom scrollbar for form area */
  .overflow-y-auto::-webkit-scrollbar {
    width: 6px;
  }

  .overflow-y-auto::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  .overflow-y-auto::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }

  .overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }

  .object-plot-fields-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
  }

  @media (min-width: 1100px) {
    .object-plot-fields-grid:not(.expanded) {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  .object-plot-form-field-grid {
    width: 100%;
    min-height: 90px;
    max-height: 110px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    overflow: hidden;
  }

  .array-plot-builder-grid-span,
  .object-plot-builder-grid-span,
  .json-plot-configuration-grid-span {
    grid-column: 1 / -1 !important;
    min-height: unset;
    max-height: unset;
    overflow: visible;
  }

  .radio-icon-center {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    font-size: 18px;
  }
</style>
